<template>
  <b-modal
    :id="modalId"
    v-model="showModal"
    hide-footer
    hide-header
    centered
    no-close-on-backdrop
  >
    <div class="p-1">
      <!-- Header -->
      <div class="flex items-center justify-between mb-1">
        <h4 class="text-base font-semibold text-[#333333] mb-0">
          {{ idBank ? 'Edit' : 'Tambah' }} Rekening
        </h4>
        <b-button
          variant="link"
          class="!p-0 text-[#828282] hover:text-[#333333]"
          @click="onCancel"
        >
          <feather-icon
            icon="XIcon"
            size="20"
          />
        </b-button>
      </div>

      <!-- Form -->
      <b-form @submit.prevent="onSubmit">
        <!-- 1. Nama Bank -->
        <b-form-group
          label-for="modal-select-bank"
          label-class="text-sm font-medium text-[#333333] mb-0.5"
        >
          <template #label>
            Nama Bank<span class="text-danger">*</span>
          </template>
          <v-select
            id="modal-select-bank"
            v-model="selectedBank"
            placeholder="Ketik untuk mencari"
            required
            :options="bankList"
            label="name"
          >
            <template #search="{ attributes, events }">
              <input
                class="vs__search"
                :required="!selectedBank"
                v-bind="attributes"
                v-on="events"
              >
            </template>
          </v-select>
        </b-form-group>

        <!-- 2. Nomor Rekening -->
        <b-form-group
          label-for="modal-input-account-no"
          label-class="text-sm font-medium text-[#333333] mb-0.5"
        >
          <template #label>
            Nomor Rekening<span class="text-danger">*</span>
          </template>
          <b-form-input
            id="modal-input-account-no"
            v-model="localAccountNumber"
            required
            type="number"
            placeholder="Masukkan nomor rekening"
            @keydown.native="preventEInput"
          />
        </b-form-group>

        <!-- 3. Nama Pemilik Rekening -->
        <b-form-group
          label-for="modal-input-account-owner"
          label-class="text-sm font-medium text-[#333333] mb-0.5"
        >
          <template #label>
            Nama Pemilik Rekening<span class="text-danger">*</span>
          </template>
          <b-form-input
            id="modal-input-account-owner"
            v-model="localAccountOwner"
            required
            type="text"
            placeholder="Masukkan nama pemilik rekening"
          />
        </b-form-group>

        <!-- Action Buttons -->
        <div class="flex items-center gap-2 mt-2">
          <b-button
            variant="outline-secondary"
            class="flex-1"
            :disabled="isLoading"
            @click="onCancel"
          >
            Batal
          </b-button>
          <b-button
            variant="primary"
            type="submit"
            class="flex-1"
            :disabled="isLoading || !isFormValid"
          >
            <b-spinner
              v-if="isLoading"
              small
              class="mr-1"
            />
            Simpan
          </b-button>
        </div>
      </b-form>
    </div>
  </b-modal>
</template>

<script>
import VSelect from 'vue-select'
import { apiGetListBank, apiCreateBankAccount } from './service/api.bankAccount.service'

export default {
  components: {
    VSelect,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    accountNumber: {
      type: String,
      default: '',
    },
    bankName: {
      type: String,
      default: '',
    },
    accountOwner: {
      type: String,
      default: '',
    },
    idBank: {
      type: [String, Number],
      default: '',
    },
  },
  data() {
    return {
      modalId: 'modal-add-bank-account',
      bankList: [],
      isLoading: false,
      selectedBank: null,
      localAccountNumber: this.accountNumber || '',
      localAccountOwner: this.accountOwner || '',
    }
  },
  computed: {
    showModal: {
      get() {
        return this.visible
      },
      set(val) {
        if (!val) {
          this.$emit('close')
        }
      },
    },
    isFormValid() {
      return !!this.selectedBank && !!this.localAccountNumber && !!this.localAccountOwner
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.localAccountNumber = this.accountNumber || ''
        this.localAccountOwner = this.accountOwner || ''
        this.definedBank()
      }
    },
    accountNumber(newValue) {
      this.localAccountNumber = newValue || ''
    },
    accountOwner(newValue) {
      this.localAccountOwner = newValue || ''
    },
    bankName: {
      immediate: true,
      handler() {
        if (this.bankList.length > 0) {
          this.definedBank()
        }
      },
    },
    bankList: {
      immediate: true,
      handler() {
        if (this.bankName) {
          this.definedBank()
        }
      },
    },
  },
  async mounted() {
    await this.getListBank()
  },
  methods: {
    onCancel() {
      this.$emit('close')
    },
    async getListBank() {
      try {
        const { data } = await apiGetListBank()
        this.bankList = data.data || []
        if (this.bankName) {
          this.definedBank()
        }
      } catch {
        this.$toast_error({ message: 'Gagal mengambil data bank' })
      }
    },
    preventEInput(event) {
      if (['e', 'E', '+', '-'].includes(event.key)) {
        event.preventDefault()
      }
    },
    definedBank() {
      if (this.bankName && this.bankList && this.bankList.length > 0) {
        const bankToFind = this.bankList.find(
          bank => bank.name === this.bankName || bank.code === this.bankName,
        )
        if (bankToFind) {
          this.selectedBank = bankToFind
        }
      } else if (!this.bankName) {
        this.selectedBank = null
      }
    },
    async onSubmit() {
      if (!this.selectedBank) {
        this.$toast_error({ message: 'Silakan pilih bank terlebih dahulu' })
        return
      }
      if (!this.localAccountNumber || String(this.localAccountNumber).trim() === '') {
        this.$toast_error({ message: 'Nomor rekening tidak boleh kosong' })
        return
      }
      if (!this.localAccountOwner || String(this.localAccountOwner).trim() === '') {
        this.$toast_error({ message: 'Nama pemilik rekening tidak boleh kosong' })
        return
      }

      this.isLoading = true
      try {
        await apiCreateBankAccount({
          mode: this.idBank ? 'edit' : 'add',
          bank_code: this.selectedBank.code,
          bank_no: this.localAccountNumber,
          bank_name: this.selectedBank.name,
          bank_owner_name: this.localAccountOwner,
          id: this.idBank,
        })
        this.$toast_success({ message: `Rekening berhasil ${this.idBank ? 'diupdate' : 'ditambahkan'}` })
        this.$emit('refresh')
        this.$emit('close')
      } catch (error) {
        const errorMessage = error.response?.data?.message || 'Gagal menyimpan rekening'
        this.$toast_error({ message: errorMessage })
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@core/scss/vue/libs/vue-select.scss';
</style>
